import api from '../../utils/axiosConfig';
import runtime from '../runtime';

const VAPID_PUBLIC_KEY = 'BKLJM3Mq_uKjwTFQ3H9VSts1RnCSNaQ_xubPOXLyGEV2mdSf5J_Iang1_3lmxkFXROu1KLgTJR5qil5QXgYHqHM';
const permissionListeners = new Set();
const registrationListeners = new Set();

const emit = (listeners, value) => listeners.forEach((callback) => callback(value));

const createPermissionDisabledError = () => {
    const error = new Error(
        'Push notifications are disabled for Connect App 365 in this device\'s system settings. Enable notifications there, then return to the app and try again.'
    );
    error.code = 'notification-permission-disabled';
    return error;
};

const toApplicationServerKey = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from(rawData, (character) => character.charCodeAt(0));
};

export const isPushNotificationSupported = () => (
    runtime.isWeb() &&
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
);

export const isNotificationPermissionGranted = () => (
    isPushNotificationSupported() && Notification.permission === 'granted'
);

export const getPermission = () => (
    isPushNotificationSupported() ? Notification.permission : 'unsupported'
);

export const requestNotificationPermission = async () => {
    if (!isPushNotificationSupported()) throw new Error('Push notifications are not supported');
    if (Notification.permission === 'denied') throw createPermissionDisabledError();

    const permission = await Notification.requestPermission();
    if (permission === 'denied') throw createPermissionDisabledError();
    return permission;
};

export const subscribeToPushNotifications = async () => {
    if (!isPushNotificationSupported()) throw new Error('Push notifications are not supported');

    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) return existingSubscription;

    return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: toApplicationServerKey(VAPID_PUBLIC_KEY)
    });
};

export const getCurrentPushSubscription = async () => {
    if (!isPushNotificationSupported()) return null;

    try {
        const registration = await navigator.serviceWorker.getRegistration('/sw.js');
        return registration ? registration.pushManager.getSubscription() : null;
    } catch (error) {
        console.error('Error getting push subscription:', error);
        return null;
    }
};

export const unsubscribeFromPushNotifications = async () => {
    const subscription = await getCurrentPushSubscription();
    return subscription ? subscription.unsubscribe() : false;
};

export const sendSubscriptionToServer = async (subscription) => {
    await api.post('/api/notifications/subscribe', { subscription: subscription.toJSON() });
};

export const removeSubscriptionFromServer = async (subscription) => {
    const payload = typeof subscription?.toJSON === 'function' ? subscription.toJSON() : subscription;
    if (!payload?.endpoint) throw new Error('Missing subscription payload for unsubscribe');
    await api.post('/api/notifications/unsubscribe', { subscription: payload });
};

export const testPushNotification = async () => {
    await api.post('/api/notifications/test-push');
};

export const showLocalNotification = (title, options = {}) => {
    if (!isNotificationPermissionGranted()) return undefined;

    const notification = new Notification(title, {
        icon: '/logo192.png',
        badge: '/caregiver-icon.svg',
        ...options
    });

    if (options.priority !== 'critical' && options.priority !== 'high') {
        setTimeout(() => notification.close(), 5000);
    }
    return notification;
};

export const isPWAWithPushEnabled = async () => {
    if (!runtime.isStandalone()) return false;
    const subscription = await getCurrentPushSubscription();
    return isNotificationPermissionGranted() && subscription !== null;
};

export const getStatus = async () => {
    const supported = isPushNotificationSupported();
    const subscription = supported ? await getCurrentPushSubscription() : null;
    const permission = supported ? Notification.permission : 'unsupported';
    return { supported, permission, subscription, enabled: permission === 'granted' && subscription !== null };
};

export const enable = async () => {
    const permission = await requestNotificationPermission();
    emit(permissionListeners, permission);
    if (permission !== 'granted') throw new Error('Notification permission denied');
    return subscribe();
};

export const subscribe = async () => {
    const subscription = await subscribeToPushNotifications();
    await sendSubscriptionToServer(subscription);
    emit(registrationListeners, { registered: true, subscription });
    return subscription;
};

export const disable = async () => {
    const subscription = await getCurrentPushSubscription();
    if (subscription) await removeSubscriptionFromServer(subscription);
    await unsubscribeFromPushNotifications();
    emit(registrationListeners, { registered: false, subscription: null });
};

export const unsubscribe = disable;

export const onReceived = (callback) => {
    if (!isPushNotificationSupported()) return () => {};
    const handleMessage = (event) => {
        if (event.data?.type === 'NEW_NOTIFICATION') callback(event.data);
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
};

const addListener = (listeners, callback) => {
    listeners.add(callback);
    return () => listeners.delete(callback);
};

export const onPermissionChanged = (callback) => addListener(permissionListeners, callback);
export const onRegistrationChanged = (callback) => addListener(registrationListeners, callback);

export const onAction = (callback) => {
    if (!isPushNotificationSupported()) return () => {};
    const handleMessage = (event) => {
        if (event.data?.type === 'NOTIFICATION_ACTION') callback(event.data);
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
};

export default Object.freeze({
    isSupported: isPushNotificationSupported,
    getPermission,
    requestPermission: requestNotificationPermission,
    getStatus,
    enable,
    disable,
    subscribe,
    unsubscribe,
    sendTest: testPushNotification,
    showLocal: showLocalNotification,
    onReceived,
    onAction,
    onPermissionChanged,
    onRegistrationChanged
});
